import React__default, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import 'react/jsx-runtime';
import InfoCircle from '../../../node_modules/@zooplus/icons/dist/es/components/InfoCircle.js';
import { P2, Anchor } from '@zooplus/ui-components';
import { useTranslation } from '../../../hooks/use-translations.js';
import useAdobeUtils from '../../../hooks/useAdobe.js';
import { CartService } from '../../../services/CartService.js';
import { Benefits } from '../../common/benefits/Benefits.js';
import { DeliveryInterval } from '../../common/delivery-interval/DeliveryInterval.js';
import { MiniSummary } from '../../common/mini-summary/MiniSummary.js';
import { CartMovError } from '../../common/mov-error/CartMovError.js';
import { MovErrorBox } from '../../common/mov-error/MovErrorBox.js';
import { Title } from '../../common/title/Title.js';
import { ArticleList } from '../article-list/ArticleList.js';
import { ShippingFees } from '../shipping-fees/ShippingFees.js';
import { Summary } from '../summary/Summary.js';
import styles from './CartOffer.module.scss.js';
import { sameArticles } from '../../../utils/utils.js';
import '../../../node_modules/@zooplus/commons-lang/dist/es/gen/lang.js';
import { useSiteContext } from '../../../node_modules/@zooplus/commons-lang/dist/es/components/SiteProvider.js';
import '../../../node_modules/@zooplus/commons-lang/dist/es/hooks/use-translation.js';

const CartOffer = (props) => {
    const { site, language } = useSiteContext();
    const { trackCartMOVError, trackCartRemoveAutoshipment } = useAdobeUtils();
    const { t } = useTranslation(site.realm);
    const offerRef = useRef(null);
    const [articles, setArticles] = useState([]);
    const [mov, setMov] = useState(0);
    const [selectedInterval, setSelectedInterval] = useState(props.intervalSelected); //default selected interval
    const [intervalOptions, setIntervalOptions] = useState([]);
    const [shippingFees, setShippingFees] = useState(0);
    const [moneyLeftFreeShipping, setMoneyLeftFreeShipping] = useState(0);
    const [total, setTotal] = useState(0);
    const [savedMoney, setSavedMoney] = useState(0);
    const currentParams = useRef({
        articles: props.articles,
        keycloakToken: props.keycloakToken,
        siteDomain: site.domain,
        deliveryCountry: props.deliveryCountry,
        deliveryRegion: props.deliveryRegion,
        deliveryZipCode: props.deliveryZipCode,
    });
    useEffect(() => {
        const params = currentParams.current;
        if (params.articles && params.siteDomain) {
            CartService(params.keycloakToken)
                .getAutoshipmentArticles(params.articles, params.siteDomain, language, params.deliveryCountry, params.deliveryRegion)
                .then((response) => {
                if (response) {
                    setArticles(response.cartArticles);
                    setMov(response.mov);
                    const options = [];
                    for (let i = response.allowedInterval.minWeeks; i <= response.allowedInterval.maxWeeks; i++) {
                        options.push(i);
                    }
                    setIntervalOptions(options);
                    setShippingFees(response.shippingFees);
                    setMoneyLeftFreeShipping(response.moneyLeftFreeShipping);
                    setTotal(response.total);
                    setSavedMoney(response.totalDiscount);
                    const subtotal = response.cartArticles.reduce((sum, current) => sum + current.bestPrice * current.quantity, 0);
                    if (response.mov - subtotal > 0) { // Mov error
                        trackCartMOVError();
                        if (props.checkIntervalSelected && !props.intervalSelected) { // toggle_force_interval On and NO interval selected
                            props.setIsSubscriptionValid(true);
                        }
                        else if (props.checkIntervalSelected && props.intervalSelected) { // toggle_force_interval On and interval
                            props.setIsSubscriptionValid(true);
                        }
                    }
                    else if (props.checkIntervalSelected && response.mov - subtotal <= 0 && !props.intervalSelected) { // no mov error, toggle_force_interval On, and no interval
                        props.setIsSubscriptionValid(false);
                    }
                }
            });
        }
    }, [currentParams.current]);
    useEffect(() => {
        const newParams = {
            articles: props.articles,
            keycloakToken: props.keycloakToken,
            siteDomain: site.domain,
            deliveryCountry: props.deliveryCountry,
            deliveryRegion: props.deliveryRegion,
            deliveryZipCode: props.deliveryZipCode,
        };
        if (!sameArticles(currentParams.current.articles, newParams.articles) ||
            currentParams.current.keycloakToken !== newParams.keycloakToken ||
            currentParams.current.siteDomain !== newParams.siteDomain ||
            currentParams.current.deliveryCountry !== newParams.deliveryCountry ||
            currentParams.current.deliveryRegion !== newParams.deliveryRegion ||
            currentParams.current.deliveryZipCode !== newParams.deliveryZipCode)
            currentParams.current = newParams;
    }, [
        props.articles,
        props.keycloakToken,
        site.domain,
        props.deliveryCountry,
        props.deliveryRegion,
        props.deliveryZipCode,
    ]);
    const ausTotal = articles.reduce((sum, current) => sum + current.bestPrice * current.quantity, 0);
    const moneyLeft = mov - ausTotal;
    const errorTranslation = moneyLeft <= 0 ? undefined : t('checkout.mov-error.mini-summary-text');
    useEffect(() => {
        // Case of offer with no errors
        if (moneyLeft <= 0 && selectedInterval) { // No MOV and interval
            // This sends to checkout cart the selected interval in days
            props.sendNewInterval({ subscriptionInterval: selectedInterval * 7 });
            if (props.checkIntervalSelected) { // toggle_force_interval is ON
                props.setIsSubscriptionValid(true);
            }
        }
    }, [selectedInterval]);
    useEffect(() => {
        if (props.checkIntervalSelected) {
            const hashInUrl = window.location.hash;
            if (hashInUrl === '#subscription-offer-cart-no-interval') {
                const element = document.querySelector(hashInUrl);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }
        }
    }, []);
    useEffect(() => {
        if (props.checkIntervalSelected && moneyLeft > 0 && !selectedInterval) { // toggle_force_interval ON, MOV error and no interval
            props.setIsSubscriptionValid(true);
        }
    }, []);
    return (React__default.createElement("div", { className: styles.wrapper, ref: offerRef, id: moneyLeft > 0 ? "subscription-offer-cart-mov" : props.intervalSelected ? "subscription-offer-cart" : "subscription-offer-cart-no-interval" },
        React__default.createElement(Title, { minifiedLearnMore: moneyLeft > 0 }),
        React__default.createElement("div", { className: styles.cartOfferBoxBody },
            moneyLeft > 0 ? (React__default.createElement(CartMovError, { size: 'normal', mov: mov, moneyLeft: moneyLeft, currency: articles[0].currency })) : (React__default.createElement(Benefits, null)),
            moneyLeft <= 0 && articles?.length > 0 && (React__default.createElement(DeliveryInterval, { options: intervalOptions, defaultInterval: selectedInterval, setSelectedInterval: setSelectedInterval, checkIntervalSelected: props.checkIntervalSelected })),
            React__default.createElement(ArticleList, { articles: articles }),
            moneyLeft <= 0 && articles?.length > 0 && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(ShippingFees, { shippingFees: shippingFees, moneyLeftToFreeShipping: moneyLeftFreeShipping, currency: articles[0].currency }),
                articles?.some((article) => article.bestPriceWithoutAus != article.originalPrice) && (React__default.createElement("div", { className: `${styles.discountDisclaimer} ${moneyLeftFreeShipping > 0 && styles.disclaimerMargin}` },
                    React__default.createElement(InfoCircle, { "aria-hidden": "true" }),
                    React__default.createElement(P2, { className: styles.discountDisclaimerText }, t('checkout.common.discount-disclaimer')))),
                React__default.createElement(Summary, { total: total, savedMoney: savedMoney, interval: selectedInterval, currency: articles[0].currency }),
                React__default.createElement(Anchor, { className: styles.cartOfferRemoveAutoshipment, isBold: true, onClick: () => {
                        trackCartRemoveAutoshipment();
                        props.removeAutoshipment();
                    } }, t('checkout.cart-offer.remove-aus')))),
            props.stickySection &&
                articles?.length > 0 &&
                createPortal(moneyLeft > 0 ? (React__default.createElement(MovErrorBox, { mov: mov, moneyLeft: moneyLeft, currency: articles[0].currency })) : (React__default.createElement(MiniSummary, { interval: selectedInterval, products: articles.length, offerElement: offerRef.current, errorTranslation: errorTranslation, cta: "cart" })), props.stickySection))));
};

export { CartOffer };
//# sourceMappingURL=CartOffer.js.map
